import { z } from 'zod';
import { API_BASE_URL, MASTRA_BASE_URL } from '@/constants';
import {
  finaliseResponse,
  meeting,
  tenantConfig,
  user,
  type FinaliseResponse,
  type Meeting,
  type TaskConfig,
  type TenantConfig,
  type User,
  type UpdateSuggestionRequest,
  type UpdateActionRequest,
  summary,
  type Summary,
  type BatchUpdateActionsRequest,
  type BatchUpdateSuggestionsRequest,
  type CreateMeetingRequestInput,
} from '@meeting-assistant/schemas';

// TODO: Remove once tenantId is available via x-service-proxy header on the BFF directly.
export function getTenantId(): string {
  const tenantIdFromEnv = import.meta.env.VITE_TENANT_ID;

  // In local development, allow an explicit env override so testing outside
  // the XPLAN parent app can force tenant context deterministically.
  if (tenantIdFromEnv && import.meta.env.DEV) {
    return tenantIdFromEnv;
  }

  return globalThis.deployConfig?.tenantId ?? '';
}

/**
 * JSON replacer that works around Xplan's Date.prototype.toJSON pollution.
 * Xplan overrides toJSON to return {_type:"Date", _val:"..."} instead of an
 * ISO string, which breaks our BFF's Zod validation. This replacer detects
 * that shape and extracts the ISO string.
 */
function jsonReplacer(_key: string, value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    '_type' in value &&
    (value as { _type: unknown })._type === 'Date' &&
    '_val' in value
  ) {
    return (value as { _val: string })._val;
  }
  return value;
}

export function safeStringify(body: unknown): string {
  return JSON.stringify(body, jsonReplacer);
}

const BODY_METHODS = new Set(['POST', 'PUT', 'PATCH']);

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const tenantId = getTenantId();

  const headers = new Headers(init.headers);
  if (BODY_METHODS.has(method) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (tenantId) {
    headers.set('Tenant-Id', tenantId);
  }

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new Error(
      (json as { message?: string } | null)?.message ??
        `Request failed (${res.status})`,
    );
  }
  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: safeStringify(body) });
}

async function patch(path: string, body: unknown): Promise<void> {
  await apiFetch<void>(path, { method: 'PATCH', body: safeStringify(body) });
}

export async function getMeetings(): Promise<Meeting[]> {
  const json = await get('/meetings');
  return z.array(meeting).parse(json);
}

export async function getMeeting(meetingId: string): Promise<Meeting> {
  const json = await get(`/meetings/${encodeURIComponent(meetingId)}`);
  return meeting.parse(json);
}

export async function getTenantConfig(): Promise<TenantConfig> {
  const json = await get('/config');
  return tenantConfig.parse(json);
}

export async function getUsers(): Promise<User[]> {
  const json = await get('/users');
  return z.array(user).parse(json);
}

export async function postSummary(meetingId: string): Promise<Summary> {
  const json = await post(
    `/meetings/${encodeURIComponent(meetingId)}/summary`,
    {},
  );
  return summary.parse(json);
}

export async function uploadTranscript(
  meetingId: string,
  transcript: string,
): Promise<Meeting> {
  const json = await post(
    `/meetings/${encodeURIComponent(meetingId)}/transcript`,
    { transcript },
  );
  return meeting.parse(json);
}

function mastraUrl(meetingId: string, path: string, clientId: string): string {
  const params = new URLSearchParams({ clientId });
  return `${MASTRA_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/${path}?${params}`;
}

async function mastraFetch<T>(
  url: string,
  label: string,
  init?: RequestInit,
): Promise<T> {
  const tenantId = getTenantId();
  const headers = new Headers(init?.headers);
  if (tenantId) {
    headers.set('Tenant-Id', tenantId);
  }
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new Error(
      (json as { error?: string } | null)?.error ??
        `${label} failed (${res.status})`,
    );
  }
  return res.json() as Promise<T>;
}

export async function postMastraAnalyse(
  meetingId: string,
  clientId: string,
  templateId = 1,
  taskConfig?: TaskConfig,
): Promise<{ meetingId: string; status: string }> {
  const url = mastraUrl(meetingId, 'analyse', clientId);
  const tenantId = getTenantId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (tenantId) {
    headers['Tenant-Id'] = tenantId;
  }
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      templateId,
      taskTypes: taskConfig?.type,
      taskSubtypes: taskConfig?.subtype,
    }),
  });
  if (res.status === 409) {
    return { meetingId, status: 'processing' };
  }
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new Error(
      (json as { error?: string } | null)?.error ??
        `Analyse failed (${res.status})`,
    );
  }
  return res.json() as Promise<{ meetingId: string; status: string }>;
}

export async function getMastraResults(
  meetingId: string,
  clientId: string,
): Promise<{
  status: string;
  error?: string;
  summary: { content: string; lastModified: string } | null;
  actions: unknown[];
  changes: unknown[];
}> {
  return mastraFetch(mastraUrl(meetingId, 'results', clientId), 'Results');
}

export async function postFinalise(
  meetingId: string,
): Promise<FinaliseResponse> {
  const json = await post(
    `/meetings/${encodeURIComponent(meetingId)}/finalise`,
    {},
  );
  return finaliseResponse.parse(json);
}

export async function patchSuggestion(
  meetingId: string,
  suggestionId: string,
  body: UpdateSuggestionRequest,
): Promise<void> {
  await patch(
    `/meetings/${encodeURIComponent(meetingId)}/suggestions/${encodeURIComponent(suggestionId)}`,
    body,
  );
}

export async function patchAllSuggestions(
  meetingId: string,
  body: BatchUpdateSuggestionsRequest,
): Promise<void> {
  await patch(`/meetings/${encodeURIComponent(meetingId)}/suggestions`, body);
}

export async function patchAction(
  meetingId: string,
  actionId: string,
  body: UpdateActionRequest,
): Promise<void> {
  await patch(
    `/meetings/${encodeURIComponent(meetingId)}/actions/${encodeURIComponent(actionId)}`,
    body,
  );
}

export async function patchAllActions(
  meetingId: string,
  body: BatchUpdateActionsRequest,
): Promise<void> {
  await patch(`/meetings/${encodeURIComponent(meetingId)}/actions`, body);
}

export interface ClientListItem {
  id: number;
  name: string;
}

const clientListItem = z.object({
  id: z.number(),
  name: z.string(),
});

export async function getClients(q?: string): Promise<ClientListItem[]> {
  const path = q ? `/clients?q=${encodeURIComponent(q)}` : '/clients';
  const json = await get(path);
  return z.array(clientListItem).parse(json);
}

export async function createMeeting(
  body: CreateMeetingRequestInput,
): Promise<Meeting> {
  const json = await post('/meetings', body);
  return meeting.parse(json);
}
